import { businessDate } from './smartReporting';
import { deliveryStatus, isDeliveryReviewed, itemDelivery } from './delivery';
import { productIndex, referenceId, validMappedProduct } from './productMapping';
import { toPaise, fromPaise, sumPaise } from './inr';

const dayNumber = day => Date.parse(`${day}T00:00:00Z`) / 86400000;
const amount = value => { try { return fromPaise(toPaise(value)); } catch { return null; } };
const sum = values => fromPaise(sumPaise(values.filter(value => value !== null).map(value => toPaise(value))));
export const priorityFor = age => age === null ? 'Unknown' : age > 60 ? 'Critical' : age > 30 ? 'High' : 'Normal';
export const ageBucket = age => age === null ? 'Unknown' : age <= 15 ? '0–15' : age <= 30 ? '16–30' : age <= 45 ? '31–45' : age <= 60 ? '46–60' : 'Over 60';
const rank = { Critical: 3, High: 2, Normal: 1, Unknown: 0 };
export const invoiceDestination = (invoice, panel = 'view') => `/reports/running-sales?invoiceId=${encodeURIComponent(invoice._id)}&panel=${panel}`;

export function buildOrderPriorities(invoices, products, now = new Date()) {
  const today = businessDate(now); const index = productIndex(products); let invalidMoney = 0, unknownDates = 0;
  const orders = invoices.map(invoice => {
    const reviewed = isDeliveryReviewed(invoice);
    const date = businessDate(invoice.invoiceDate || invoice.date);
    const age = date && date <= today ? dayNumber(today) - dayNumber(date) : null;
    if (age === null) unknownDates++;
    const total = amount(invoice.totalAmount), received = amount(invoice.amountReceived);
    let due = amount(invoice.balanceDue);
    // Derive only when absent, never replace a stored invalid balance or round it.
    if (invoice.balanceDue == null && total !== null && received !== null && received <= total) due = fromPaise(toPaise(total) - toPaise(received));
    if ([total, received, due].some(value => value === null)) invalidMoney++;
    const items = (Array.isArray(invoice.items) ? invoice.items : []).map((rawItem, position) => {
      const item = rawItem || {};
      const product = validMappedProduct(item, invoice.organization, index);
      const pending = itemDelivery(item).remaining;
      const price = amount(item.amount);
      let value = null;
      if (price !== null && pending !== null) { try { value = fromPaise(toPaise(price) * pending); } catch { /* Unsupported values remain unknown. */ } }
      return { key: `${invoice._id}:${position}`, itemId: item._id, name: product?.name || item.label || 'Unnamed item', originalName: item.label,
        sku: product?.sku || '', productId: product ? referenceId(product._id) : null, productKey: product ? `${invoice.organization}:${referenceId(product._id)}` : 'unmapped',
        qty: item.qty, delivered: item.deliveredQty, pending, value };
    });
    const knownPending = items.reduce((n,item) => n + (item.pending ?? 0), 0);
    const pending = items.length && items.every(item => item.pending !== null) ? knownPending : null;
    const ordered = items.length && items.every(item => Number.isSafeInteger(item.qty) && item.qty > 0) ? items.reduce((n, item) => n + item.qty, 0) : null;
    const open = deliveryStatus(invoice) !== 'Fully Delivered';
    const nearCompletion = open && pending !== null && pending > 0 && (pending <= 2 || (ordered !== null && pending * 5 <= ordered));
    return { id: String(invoice._id), invoice, number: invoice.invoiceNumber || String(invoice._id), customer: invoice.customerName || 'Unknown customer',
      organization: invoice.organization || 'Unknown', date, age, bucket: ageBucket(age), priority: priorityFor(age), reviewed, status: deliveryStatus(invoice),
      items, pending, knownPending, ordered, open, total, received, due, nearCompletion, highValueQuick: nearCompletion && total !== null && total >= 10000 };
  });
  return { orders, today, invalidMoney, unknownDates };
}

export function filterOrders(orders, filters) {
  const search = (filters.search || '').trim().toLowerCase(); const customer = (filters.customer || '').trim().toLowerCase();
  return orders.filter(order => {
    if (filters.organization !== 'All' && order.organization !== filters.organization) return false;
    if (search && !`${order.number} ${order.customer}`.toLowerCase().includes(search)) return false;
    if (customer && !order.customer.toLowerCase().includes(customer)) return false;
    if (filters.product && !order.items.some(item => item.productKey === filters.product)) return false;
    if (filters.age && (filters.age.startsWith('>') ? order.age === null || order.age <= Number(filters.age.slice(1)) : order.bucket !== filters.age)) return false;
    if (filters.priority && order.priority !== filters.priority) return false;
    if (filters.payment === 'Due' && !(order.due > 0)) return false;
    if (filters.payment === 'No Due' && order.due !== 0) return false;
    if (filters.payment === 'Significant' && !(order.due >= 10000)) return false;
    if (filters.delivery === 'Action required' && !order.open) return false;
    if (filters.delivery && !['All','Action required'].includes(filters.delivery) && order.status !== filters.delivery) return false;
    return true;
  });
}
export function orderMetrics(orders) {
  const open = orders.filter(order => order.open);
  return { open: open.length, overdue: open.filter(o => o.age > 30).length, critical: open.filter(o => o.age > 60).length,
    units: open.reduce((n,o) => n+o.knownPending,0), outstanding: sum(open.map(o => o.due)), unknownDues: open.filter(o => o.due === null).length };
}
export function focusedOrders(orders, focus) {
  if (focus === 'open' || focus === 'units') return orders.filter(o => o.open);
  if (focus === 'outstanding') return orders.filter(o => o.open && o.due > 0);
  if (focus === 'overdue') return orders.filter(o => o.open && o.age > 30);
  if (focus === 'critical') return orders.filter(o => o.open && o.age > 60);
  return orders;
}
export function compareOrderPriority(a, b) {
  return Number(b.open)-Number(a.open) || (b.age ?? -1) - (a.age ?? -1) || Number(b.highValueQuick)-Number(a.highValueQuick) || (b.due ?? -1)-(a.due ?? -1) || a.number.localeCompare(b.number);
}
export function collectionOpportunities(orders) {
  return orders.filter(o => o.open && o.due > 0).sort((a,b) => rank[b.priority]-rank[a.priority] || Number(b.nearCompletion)-Number(a.nearCompletion) ||
    (a.pending ?? Infinity)-(b.pending ?? Infinity) || b.due-a.due || (b.age ?? -1)-(a.age ?? -1) || a.number.localeCompare(b.number));
}
export function productionDemand(orders, productFilter = '') {
  const groups = new Map(), unmapped = [], items = [];
  for (const order of orders) {
    if (!order.open) continue;
    for (const item of order.items) {
      if (!(item.pending > 0) || (productFilter && item.productKey !== productFilter)) continue;
      const row = { ...item, order }; items.push(row);
      if (!item.productId) { unmapped.push(row); continue; }
      if (!groups.has(item.productKey)) groups.set(item.productKey, { key: item.productKey, name: item.name, sku: item.sku, productId: item.productId, quantity: 0, orders: new Map(), rows: [], oldest: null });
      const group = groups.get(item.productKey); group.quantity += item.pending; group.rows.push(row); group.orders.set(order.id, order);
      if (order.age !== null) group.oldest = Math.max(group.oldest ?? 0, order.age);
    }
  }
  const products = [...groups.values()].map(group => {
    const linked = [...group.orders.values()];
    const completes = linked.filter(order => order.items.filter(item => item.pending > 0).every(item => item.productKey === group.key)).length;
    return { ...group, orderCount: linked.length, priority: priorityFor(group.oldest), value: group.rows.every(row => row.value === null) ? null : sum(group.rows.map(row => row.value)),
      unknownValues: group.rows.some(row => row.value === null), linkedDues: sum(linked.map(o => o.due)), completes };
  }).sort((a,b) => rank[b.priority]-rank[a.priority] || b.orderCount-a.orderCount || b.completes-a.completes || (b.oldest ?? -1)-(a.oldest ?? -1) || b.linkedDues-a.linkedDues || a.key.localeCompare(b.key));
  return { products, unmapped, items };
}
